import { mergeDeepRight, mergeLeft } from 'ramda';
import { combine } from 'effector';

import { widgetsDomain, domainKey } from './constants';
import { getWidgetsFx, getPortalSettingsFx } from './effects';
import { settingsSelector } from './selectors';
import type {
  IWidgetMetadata,
  IPlatformWidgetsStore,
  IPortalSettingsStore,
  DashboardWidgetsStoreT,
  NavGraphNodeT
} from './types';

const storeWidgetsInitialState: DashboardWidgetsStoreT = {};

export const $storeWidgets = widgetsDomain.createStore(storeWidgetsInitialState, {
  name: `${domainKey}/stores/storeWidgets`,
  sid: `${domainKey}/stores/storeWidgets`
});

const platformWidgetsInitialState: IPlatformWidgetsStore = {
  byId: {},
  byDomain: {},
  loaded: false,
  privateLoaded: false,
  publicLoaded: false
};

export const $platformWidgets = widgetsDomain.createStore(platformWidgetsInitialState, {
  name: `${domainKey}/stores/platformWidgets`,
  sid: `${domainKey}/stores/platformWidgets`
});

// @ts-expect-error
$storeWidgets.on(getWidgetsFx.doneData, (state, { dashboard }) => {
  return mergeDeepRight<DashboardWidgetsStoreT, Record<string, IWidgetMetadata>>(state, dashboard);
});

$platformWidgets.on(getWidgetsFx.done, (state, { params: { type } }) => {
  const result = { ...state };
  if (type === 'private') {
    result.privateLoaded = true;
  } else if (type === 'public') {
    result.publicLoaded = true;
  }

  return result;
});

// @ts-expect-error
$platformWidgets.on(getWidgetsFx.doneData, (state, { platform }) => {
  return {
    ...mergeDeepRight(state, platform),
    loaded: true
  };
});

$platformWidgets.on(getWidgetsFx.fail, (state, { params: { type } }) => {
  const result = { ...state, loaded: false };
  if (type === 'private') {
    result.privateLoaded = false;
  } else if (type === 'public') {
    result.publicLoaded = false;
  }

  return result;
});

export const $allWidgets = combine(
  $storeWidgets,
  $platformWidgets,
  (dashboardWidgets, platformWidgets) => {
    return mergeLeft(dashboardWidgets, platformWidgets.byId);
  }
);

const portalSettingsInitialState: IPortalSettingsStore = {
  navgraph: {
    default: {},
    personal: {}
  },
  flatNavGraph: {
    system: new Map<string, NavGraphNodeT>(),
    personal: new Map<string, NavGraphNodeT>()
  },
  widgets: {
    default: {},
    personal: {}
  }
};

export const $portalSettings = widgetsDomain.createStore(portalSettingsInitialState, {
  name: `${domainKey}/stores/portalSettings`,
  sid: `${domainKey}/stores/portalSettings`
});

$portalSettings.on(getPortalSettingsFx.doneData, (state, portalSettings) => {
  const navGraph = settingsSelector<'navgraph'>('navgraph', portalSettings);
  const userRole = localStorage.getItem('user-role');
  // one of root nodes for now
  const defaultDashboardPath = Object.keys(navGraph).find(
    (nodeKey) => navGraph[nodeKey].meta?.isDefault
  );
  const sanitizedPath = defaultDashboardPath || 'home';

  const dashboardId =
    // @ts-expect-error
    navGraph[sanitizedPath].role[userRole] ?? navGraph[sanitizedPath].role.default ?? null;

  window.sharedHostGateway.stateSharing.shareValue({
    storePath: ['defaultDashboard'],
    sharedValue: {
      id: dashboardId,
      path: `/${sanitizedPath}`
    }
  });

  return portalSettings;
});
